import { useId } from 'react'
import type { ChangeEvent, InputHTMLAttributes, ReactNode } from 'react'

/**
 * Controles de formulário.
 *
 * A mensagem de erro fica **abaixo** do campo e é ligada por
 * `aria-describedby`, com `aria-invalid` no controle: um leitor de tela anuncia
 * o motivo junto com o campo, e não como um aviso solto no topo do formulário.
 *
 * Erro é sempre texto — a borda vermelha é reforço. Um campo marcado só por cor
 * não diz o que está errado, nem para quem enxerga a cor.
 */

const CONTROL =
  'w-full min-w-0 border bg-inset px-2 py-1 text-base text-fg placeholder:text-fg-muted disabled:cursor-not-allowed disabled:opacity-50'

const CONTROL_OK = 'border-line-strong'
const CONTROL_BAD = 'border-crit'

export function Field({
  label,
  htmlFor,
  hint,
  error,
  required = false,
  children,
  className = '',
}: {
  label: string
  htmlFor: string
  /** Regra que o operador precisa saber ANTES de digitar. */
  hint?: ReactNode
  error?: string | null
  required?: boolean
  children: ReactNode
  className?: string
}) {
  const invalid = error != null && error !== ''

  return (
    <div className={`flex min-w-0 flex-col gap-1 ${className}`}>
      <label
        htmlFor={htmlFor}
        className="text-micro font-medium uppercase tracking-label text-fg-secondary"
      >
        {label}
        {required && (
          <span className="ml-1 text-fg-muted" title="Obrigatório">
            *
          </span>
        )}
      </label>
      {children}
      {hint !== undefined && !invalid && (
        <p id={`${htmlFor}-hint`} className="text-micro leading-snug text-fg-muted">
          {hint}
        </p>
      )}
      {invalid && (
        <p id={`${htmlFor}-error`} className="text-micro leading-snug text-crit">
          {error}
        </p>
      )}
    </div>
  )
}

interface TextFieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'onChange' | 'id'> {
  label: string
  value: string
  onValueChange: (value: string) => void
  hint?: ReactNode
  error?: string | null
  /** Campo monoespaçado: chave, fingerprint, identificador. */
  mono?: boolean
}

export function TextField({
  label,
  value,
  onValueChange,
  hint,
  error,
  mono = false,
  required = false,
  className = '',
  ...rest
}: TextFieldProps) {
  const id = useId()
  const invalid = error != null && error !== ''
  const describedBy = invalid ? `${id}-error` : hint !== undefined ? `${id}-hint` : undefined

  return (
    <Field
      label={label}
      htmlFor={id}
      hint={hint}
      error={error ?? null}
      required={required}
      className={className}
    >
      <input
        id={id}
        value={value}
        required={required}
        aria-invalid={invalid || undefined}
        aria-describedby={describedBy}
        onChange={(event: ChangeEvent<HTMLInputElement>) => onValueChange(event.target.value)}
        className={`${CONTROL} ${invalid ? CONTROL_BAD : CONTROL_OK} ${mono ? 'font-mono' : ''}`}
        {...rest}
      />
    </Field>
  )
}

export function SelectField({
  label,
  value,
  onValueChange,
  options,
  hint,
  error,
  disabled = false,
  className = '',
}: {
  label: string
  value: string
  onValueChange: (value: string) => void
  options: ReadonlyArray<{ value: string; label: string }>
  hint?: ReactNode
  error?: string | null
  disabled?: boolean
  className?: string
}) {
  const id = useId()
  const invalid = error != null && error !== ''

  return (
    <Field label={label} htmlFor={id} hint={hint} error={error ?? null} className={className}>
      <select
        id={id}
        value={value}
        disabled={disabled}
        aria-invalid={invalid || undefined}
        onChange={(event) => onValueChange(event.target.value)}
        className={`${CONTROL} ${invalid ? CONTROL_BAD : CONTROL_OK}`}
      >
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </Field>
  )
}

/** Caixa de marcação com rótulo clicável e a consequência escrita embaixo. */
export function CheckboxField({
  label,
  checked,
  onCheckedChange,
  hint,
  disabled = false,
}: {
  label: string
  checked: boolean
  onCheckedChange: (checked: boolean) => void
  hint?: ReactNode
  disabled?: boolean
}) {
  const id = useId()

  return (
    <div className="flex min-w-0 flex-col gap-0.5">
      <label htmlFor={id} className="flex items-center gap-2 text-base text-fg-secondary">
        <input
          id={id}
          type="checkbox"
          checked={checked}
          disabled={disabled}
          onChange={(event) => onCheckedChange(event.target.checked)}
          className="size-3.5 shrink-0 disabled:opacity-50"
          style={{ accentColor: 'var(--c-accent)' }}
        />
        {label}
      </label>
      {hint !== undefined && <p className="pl-6 text-micro leading-snug text-fg-muted">{hint}</p>}
    </div>
  )
}

/** Busca de barra de ferramentas: não é campo de formulário, não leva rótulo em bloco. */
export function SearchInput({
  value,
  onValueChange,
  placeholder,
  label,
  className = '',
}: {
  value: string
  onValueChange: (value: string) => void
  placeholder: string
  label: string
  className?: string
}) {
  const id = useId()

  return (
    <div className={`flex min-w-0 items-center ${className}`}>
      <label htmlFor={id} className="sr-only">
        {label}
      </label>
      <input
        id={id}
        type="search"
        value={value}
        placeholder={placeholder}
        onChange={(event) => onValueChange(event.target.value)}
        className={`${CONTROL} ${CONTROL_OK} py-0.5 text-sm`}
      />
    </div>
  )
}

/** Alternador de filtro: ligado é uma borda de acento, não só um tom diferente. */
export function FilterToggle({
  label,
  pressed,
  onPressedChange,
  title,
}: {
  label: string
  pressed: boolean
  onPressedChange: (pressed: boolean) => void
  title?: string
}) {
  return (
    <button
      type="button"
      aria-pressed={pressed}
      title={title}
      onClick={() => onPressedChange(!pressed)}
      className={`shrink-0 border px-1.5 py-0.5 text-micro uppercase tracking-label ${
        pressed
          ? 'border-accent text-accent'
          : 'border-line-strong text-fg-muted hover:bg-inset hover:text-fg'
      }`}
    >
      {label}
    </button>
  )
}
